import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { createHtmlElement, empty, htmlStringToElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsInputTextEditor } from "@cervval.js/client-html5-ui/src/editors/inputTextEditor/inputTextEditor";
import { I18nDigitCode } from "./digitCodeComponent.i18n";

export interface DigitCodeOptions {
    /** Number of digits */
    nbrDigits: number;
    /** Function to call when all digits are full */
    onComplete: (full: boolean) => void;
    /** Title */
    inputName?: string;
}

export const defaultDigitCodeOptions: DigitCodeOptions = {
    nbrDigits: 4,
    onComplete: () => { }
};

@CustomElement({
    name: "digit-code-component",
    style: require("./digitCodeComponent.scss"),
    template: require("./digitCodeComponent.html"),
    i18n: require("./digitCodeComponent.i18n").I18n
})
export class DigitCodeComponent<I18n extends I18nDigitCode = I18nDigitCode> extends WebComponent<I18n> {

    @TypedWebComponentOptions(defaultDigitCodeOptions)
    public readonly options!: WebComponentOptions<DigitCodeOptions>;

    private inputs: HTMLInputElement[] = [];

    private finalCode: string = "";

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInputTextEditor] })
    public readonly components!: {
        digitCodeContainer: HTMLDivElement,
        name: HTMLDivElement
    };

    constructor(options?: Partial<DigitCodeOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** Init component */
    public override init(): Promise<void> | void {

    }

    /**
     * Refresh of the component
     */
    public override refresh(): void {
        this._render();
    }

    /**
     * Render component
     */
    protected _render(): void {
        empty(this.components.digitCodeContainer);
        empty(this.components.name);
        if (this.options.inputName) {
            const label = htmlStringToElement(`<label class="form-label">${this.options.inputName}</label>`);
            if (label) {
                this.components.name.append(label);
            }
        }
        this.inputs = [];

        const values = new Array(this.options.nbrDigits).fill("");

        for (let i = 0; i < this.options.nbrDigits; i++) {
            const input = createHtmlElement("input", {
                attributes: { "type": "tel", "max-length": "1", "placeholder": "_" },
                cssClass: ["digit-code", `digit-${i}`]
            });

            // Event listener to handle value input
            input.addEventListener("input", (e) => this._handleInput(i, this.options.nbrDigits, values));

            // Event listener for backspace
            input.addEventListener("keydown", (e) => this._handleBackSpace(i, e));

            this.components.digitCodeContainer.append(input);
            this.inputs.push(input);
        }

        // Focus the first input initially
        this.inputs[0].focus();
    }

    protected _handleInput(index: number, numDigits: number, values: string[]): void {
        const value = this.inputs[index].value;
        if (/^\d$/.test(value)) { // Only allow numeric input
            values[index] = value;

            // Move to the next input if it’s not the last one
            if (index < numDigits - 1) {
                this.inputs[index + 1].focus();
            }

            // Check if all fields are filled
            if (values.every((val) => val !== "")) {
                const finalCode = values.join("");
                this.finalCode = finalCode;
                this.options.onComplete(true);
            }
        } else {
            // Clear invalid input
            this.inputs[index].value = "";
        }
    }

    private _handleBackSpace(index: number, e: KeyboardEvent): void {
        if (e.key === "Backspace" && this.inputs[index].value === "" && index > 0) {
            // Move to the previous input on backspace if the current input is empty
            this.inputs[index - 1].focus();
            this.options.onComplete(false);
        }
    }

    /**
     * Retrieve final value
     * @returns 
     */
    public getValue(): string {
        return this.finalCode;
    }

}